import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';

export default function Privacy() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="mb-8">
          <Link to="/">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Home
            </Button>
          </Link>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-8 md:p-12">
          <h1 className="text-3xl font-bold mb-6">Privacy Policy</h1>
          <p className="text-sm text-gray-500 mb-8">Effective Date: January 1, 2024</p>

          <div className="prose prose-gray max-w-none">
            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">1. Introduction</h2>
              <p className="text-gray-600 mb-4">
                Waitlist Manager ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy 
                explains how we collect, use, disclose, and safeguard your information when you use our service.
              </p>
              <p className="text-gray-600 mb-4">
                We are based in California and comply with applicable California privacy laws, including the California 
                Consumer Privacy Act (CCPA) and the California Privacy Rights Act (CPRA).
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">2. Information We Collect</h2>
              
              <h3 className="text-lg font-medium mb-3">Personal Information</h3>
              <ul className="list-disc pl-6 text-gray-600 space-y-2 mb-4">
                <li>Name and email address</li>
                <li>Account credentials</li>
                <li>Payment information (processed securely through Stripe)</li>
                <li>Company/organization information</li>
              </ul>

              <h3 className="text-lg font-medium mb-3">Usage Data and Analytics</h3>
              <ul className="list-disc pl-6 text-gray-600 space-y-2 mb-4">
                <li>Feature usage patterns</li>
                <li>Page views and clicks</li>
                <li>Session duration and frequency</li>
                <li>Performance metrics</li>
                <li>Error logs and debugging information</li>
              </ul>

              <h3 className="text-lg font-medium mb-3">Automatically Collected Information</h3>
              <ul className="list-disc pl-6 text-gray-600 space-y-2">
                <li>IP address</li>
                <li>Browser type and version</li>
                <li>Device information</li>
                <li>Operating system</li>
                <li>Referring URLs</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">3. How We Use Your Information</h2>
              <p className="text-gray-600 mb-4">We use the collected information to:</p>
              <ul className="list-disc pl-6 text-gray-600 space-y-2">
                <li>Provide and maintain our Service</li>
                <li>Process transactions and manage subscriptions</li>
                <li>Send administrative communications</li>
                <li>Improve our platform through analytics and metrics</li>
                <li>Detect and prevent fraud or abuse</li>
                <li>Provide customer support</li>
                <li>Develop new features and services</li>
                <li>Comply with legal obligations</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">4. Platform Improvement Metrics</h2>
              <p className="text-gray-600 mb-4">
                We collect and analyze metrics to continuously improve our platform. This includes:
              </p>
              <ul className="list-disc pl-6 text-gray-600 space-y-2">
                <li>Feature adoption rates</li>
                <li>User engagement patterns</li>
                <li>Performance benchmarks</li>
                <li>Error rates and system health</li>
                <li>Conversion funnel analytics</li>
              </ul>
              <p className="text-gray-600 mt-4">
                All metrics are aggregated and anonymized when possible to protect individual privacy while helping us 
                build a better product.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">5. Data Sharing and Disclosure</h2>
              <p className="text-gray-600 mb-4">We do not sell your personal information. We may share your information with:</p>
              <ul className="list-disc pl-6 text-gray-600 space-y-2">
                <li>Service providers (e.g., Stripe for payments, AWS for hosting)</li>
                <li>Legal authorities when required by law</li>
                <li>Business partners with your consent</li>
                <li>Successor entities in case of merger or acquisition</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">6. California Privacy Rights</h2>
              <p className="text-gray-600 mb-4">
                If you are a California resident, you have the following rights under the CCPA/CPRA:
              </p>
              <ul className="list-disc pl-6 text-gray-600 space-y-2">
                <li><strong>Right to Know:</strong> Request information about personal information we collect, use, and disclose</li>
                <li><strong>Right to Delete:</strong> Request deletion of your personal information</li>
                <li><strong>Right to Opt-Out:</strong> Opt-out of the sale or sharing of personal information (we do not sell personal information)</li>
                <li><strong>Right to Correct:</strong> Request correction of inaccurate personal information</li>
                <li><strong>Right to Limit Use:</strong> Limit use and disclosure of sensitive personal information</li>
                <li><strong>Right to Non-Discrimination:</strong> Not be discriminated against for exercising privacy rights</li>
              </ul>
              <p className="text-gray-600 mt-4">
                To exercise these rights, please contact us at privacy@waitlistmanager.com.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">7. Data Security</h2>
              <p className="text-gray-600 mb-4">
                We implement appropriate technical and organizational measures to protect your information, including:
              </p>
              <ul className="list-disc pl-6 text-gray-600 space-y-2">
                <li>Encryption of data in transit and at rest</li>
                <li>Regular security audits and updates</li>
                <li>Access controls and authentication</li>
                <li>Secure data centers</li>
                <li>Employee training on data protection</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">8. Data Retention</h2>
              <p className="text-gray-600 mb-4">
                We retain your personal information for as long as necessary to provide our services and fulfill the 
                purposes outlined in this policy. When you delete your account, we will delete or anonymize your personal 
                information within 30 days, except where retention is required by law.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">9. Cookies and Tracking</h2>
              <p className="text-gray-600 mb-4">
                We use cookies and similar tracking technologies to:
              </p>
              <ul className="list-disc pl-6 text-gray-600 space-y-2">
                <li>Maintain user sessions</li>
                <li>Remember user preferences</li>
                <li>Analyze usage patterns</li>
                <li>Improve service performance</li>
              </ul>
              <p className="text-gray-600 mt-4">
                You can control cookies through your browser settings, though some features may not function properly 
                without them.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">10. Children's Privacy</h2>
              <p className="text-gray-600 mb-4">
                Our Service is not directed to individuals under 18. We do not knowingly collect personal information from 
                children. If you become aware that a child has provided us with personal information, please contact us.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">11. International Data Transfers</h2>
              <p className="text-gray-600 mb-4">
                Your information may be transferred to and processed in countries other than your own. We ensure appropriate 
                safeguards are in place for such transfers in compliance with applicable laws.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">12. Changes to This Policy</h2>
              <p className="text-gray-600 mb-4">
                We may update this Privacy Policy from time to time. We will notify you of any material changes by posting 
                the new policy on this page and updating the effective date.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">13. Contact Us</h2>
              <p className="text-gray-600 mb-4">
                For questions about this Privacy Policy or to exercise your privacy rights, please contact us at:
              </p>
              <p className="text-gray-600">
                Email: privacy@waitlistmanager.com<br />
                Address: California, United States<br />
                Data Protection Officer: dpo@waitlistmanager.com
              </p>
            </section>

            <div className="mt-12 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-800">
                <strong>Note:</strong> This is a preliminary version of our Privacy Policy pending legal review. 
                The final version will be updated once reviewed by our legal team to ensure full compliance with 
                California privacy laws and other applicable regulations.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}